import React, { useEffect, useState } from "react";
import MainRouter from "./components/ReusableComponents/MainRouter";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { useDispatch, useSelector } from "react-redux";
import HubSpotChat from "./components/HubSpotChat";
import Chat from "./Chat";

import "./App.scss";
import "./neonMode.scss";
import "./light-new.scss";
import { Toaster } from "react-hot-toast";
import { AcctokenD } from "./store/actions";
import { generateDeviceId } from "./utilities/string";
import DOBWarningModal from "./components/DOBWarningModal";
import CityWarningModal from "./components/CityWarningModal";
import CountryWarningModal from "./components/CountryWarningModal";
import { baseUrl } from "./Axios";
import axios from "axios";
import { message } from "antd";
import { getUserProfile } from "./store/reducers/userProfileReducer";

// Create a dark theme
const darkTheme = createTheme({
  palette: {
    mode: "light",
  },
});

function App() {
  // Get the selected theme from Redux state
  const theme = useSelector((state) => state.theme);
  const ACCtoken = useSelector((state) => state.ACCtoken);
  const dispatch = useDispatch();
  let deviceId = localStorage.getItem("deviceId");
  const { date_of_birth, User_id, City, Country } = useSelector(
    (state) => state.user
  );
  const [needsDob, setNeedsDob] = useState(false);
  const [needsCity, setNeedsCity] = useState(false);
  const [needsCountry, setNeedsCountry] = useState(false);
  const [loader, setLoader] = useState(false);
  useEffect(() => {
    if (!ACCtoken || !User_id?.id) {
      setNeedsDob(false);
      setNeedsCity(false);
      setNeedsCountry(false);
      return;
    }
    setNeedsDob(!date_of_birth);
    setNeedsCity(!City);
    setNeedsCountry(!Country);
  }, [ACCtoken, User_id?.id, date_of_birth, City, Country]);

  if (!deviceId) {
    deviceId = generateDeviceId(20);
    localStorage.setItem("deviceId", deviceId);
  }

  async function saveProfileField({ dob, city, country }) {
    const config = {
      headers: {
        Authorization: `Bearer ${ACCtoken}`,
      },
    };
    try {
      setLoader(true);
      const formData = new FormData();
      if (dob) formData.append("date_of_birth", dob.format("YYYY-MM-DD"));
      if (city) formData.append("City", city);
      if (country) formData.append("Country", country);
      await axios.post(`${baseUrl}/v3/user/profile/create/`, formData, config);
      await dispatch(getUserProfile());
      message.success("Profile updated successfully!");
      if (dob) setNeedsDob(false);
      if (city) setNeedsCity(false);
      if (country) setNeedsCountry(false);
    } catch (error) {
      message.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Could not save that. Please try again."
      );
    } finally {
      setLoader(false);
    }
  }

  useEffect(() => {
    const handler = (event) => {
      const { hostname } = window.location;
      let validUrl = "http://localhost:3000";

      // UAT environment
      if (hostname === "user-uat.alphacapitalgroup.uk") {
        console.log("Running in UAT environment");
        validUrl = "https://backend-dev.alphacapitalgroup.uk";
      }

      // DEV/QA environment
      if (hostname === "user-dev.alphacapitalgroup.uk") {
        console.log("Running in DEV/QA environment");
        validUrl = "https://admin-dev.alphacapitalgroup.uk";
      }

      // Production environment
      if (hostname === "app.alphacapitalgroup.uk") {
        console.log("Running in Production environment");
        validUrl = "https://super.alphacapitalgroup.uk";
      }

      console.log("event.origin:", event?.origin);

      // Security check
      if (event.origin !== validUrl) return;
      // ✅ Security check
      if (event?.origin !== validUrl) return;

      const token = event?.data?.token;
      if (token) {
        dispatch(AcctokenD(token));
      }

      window.removeEventListener("load", notifyParentReady);
      window.removeEventListener("message", handler);
    };

    const notifyParentReady = () => {
      if (window.opener) {
        // Determine the expected parent origin dynamically
        const parentOrigin =
          window.location.hostname === "user-uat.alphacapitalgroup.uk"
            ? "https://admin-uat.alphacapitalgroup.uk"
            : window.location.hostname === "user-dev.alphacapitalgroup.uk"
              ? "https://admin-dev.alphacapitalgroup.uk"
              : window.location.hostname === "app.alphacapitalgroup.uk"
                ? "https://super.alphacapitalgroup.uk"
                : "http://localhost:3000";

        console.log("Notifying parent, origin:", parentOrigin);
        window.opener.postMessage({ ready: true }, parentOrigin);
      }
    };

    // Tell parent we’re ready once page finishes loading
    window.addEventListener("load", notifyParentReady);

    // Listen for token messages
    window.addEventListener("message", handler);

    // ✅ Cleanup listeners on unmount
    return () => {
      window.removeEventListener("message", handler);
      window.removeEventListener("load", notifyParentReady);
    };
  }, [dispatch]);

  // Effect to handle context menu
  useEffect(() => {
    const handleContextmenu = (e) => {
      e.preventDefault();
    };
    document.addEventListener("contextmenu", handleContextmenu);
    return function cleanup() {
      document.removeEventListener("contextmenu", handleContextmenu);
    };
  }, []);

  // Effect to apply the selected theme
  useEffect(() => {
    // Remove existing theme classes
    document.body.classList.remove("light", "light-new");
    // Add class for the selected theme
    document.body.classList.add(theme.theme);
  }, [theme]);

  // remove all console logs
  // console.log = function () {};


  return (
    <div className="App">
      {/* ThemeProvider to apply the dark theme */}
      <ThemeProvider theme={darkTheme}>
        {/* Apply CSS baseline */}
        <CssBaseline />
        {/* Render Chat component */}
        <Chat />
        {/* Render MainRouter component */}
        <MainRouter />
        <Toaster
          style={{ zIndex: "100000000" }}
          position="top-right"
          reverseOrder={false}
        />

        <DOBWarningModal
          isOpen={needsDob}
          onOK={(dob) => saveProfileField({ dob })}
          text={"Please enter your date of birth to continue."}
          loader={loader}
        />

        <CityWarningModal
          isOpen={!needsDob && needsCity}
          onOK={(city) => saveProfileField({ city })}
          text={"Please enter your city to continue."}
          loader={loader}
        />

        <CountryWarningModal
          isOpen={!needsDob && !needsCity && needsCountry}
          onOK={(country) => saveProfileField({ country })}
          text={"Please select your country to continue."}
          loader={loader}
        />
      </ThemeProvider>
      {/* Other components */}
      {/* <Notification /> */}
      {/* <HubSpotChat/> */}
    </div>
  );
}

export default App;